/**
 * Links the logged-in userId to the current FCM token so push notifications
 * can be targeted to specific users.
 *
 * Call syncFcmUserId(userId) after a successful login, and once on app load
 * when the user is already known (e.g. from localStorage).
 *
 * React owns this link: it already knows the userId reliably (no native
 * round-trip needed), so it fetches the token from Flutter and saves the
 * pairing itself. This only has an effect inside the Flutter WebView — in a
 * plain browser getFcmToken() returns null and this is a no-op.
 */

import { FIREBASE_FUNCTIONS, callCloudFunction } from './firebaseFunctions';

type FlutterWebView = {
  callHandler: (name: string, ...args: unknown[]) => Promise<unknown>;
};

function getFlutterWebView(): FlutterWebView | null {
  if (typeof window === 'undefined') return null;
  return (
    (window as unknown as { flutter_inappwebview?: FlutterWebView }).flutter_inappwebview ?? null
  );
}

/**
 * Returns the current FCM token from Flutter, or null if not available.
 * Useful if the web app wants to store the token in its own database.
 */
export async function getFcmToken(): Promise<string | null> {
  try {
    const bridge = getFlutterWebView();
    if (!bridge) return null;
    const token = await bridge.callHandler('getFcmToken');
    return typeof token === 'string' ? token : null;
  } catch {
    return null;
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Flutter's bridge injection and its own FCM init (permission prompt + token
// fetch) both happen asynchronously and may still be in flight when React
// mounts, so getFcmToken() can legitimately return null for a few seconds.
// Retry with backoff instead of giving up on the first miss.
const TOKEN_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

/**
 * Links the current userId to the current FCM token by saving the pairing
 * directly from React. Safe to call from anywhere — no-ops outside the
 * Flutter WebView, or if Flutter never obtains a token within the retry window.
 */
export async function syncFcmUserId(userId: string): Promise<void> {
  const trimmedUserId = userId?.trim();
  if (!trimmedUserId) return;
  try {
    let token = await getFcmToken();
    for (let i = 0; !token && i < TOKEN_RETRY_DELAYS_MS.length; i++) {
      await delay(TOKEN_RETRY_DELAYS_MS[i]);
      token = await getFcmToken();
    }
    if (!token) return;
    await callCloudFunction('saveFcmToken', FIREBASE_FUNCTIONS.saveFcmToken(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fcmToken: token, userId: trimmedUserId }),
    });
  } catch {
    // Silently ignore — bridge may not be set up yet, or request failed
  }
}
